import React, { useEffect, useState } from "react";
import { onlyNumber } from "../../helpers/formats";
import { HiOutlineArrowRight } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import CustomField from "../common/customField";
import BeatLoader from "react-spinners/BeatLoader";
import {
  CurrencySymbols,
  optionsForBusinessCategory,
} from "../common/utlity/options";
import { Country, State } from "country-state-city";
import { motion } from "framer-motion";
import {
  FiBriefcase,  // Using FiBriefcase instead of FiBuilding
  FiMapPin,
  FiGlobe,
  FiDollarSign,
  FiCheck,
  FiTrendingUp,
  FiChevronRight,  // Using FiChevronRight instead of FiArrowRight
  FiPhone
} from "react-icons/fi";

const BusinessForm = ({ createBusiness, changeBusiness, business, theme = "light" }) => {
  const { t } = useTranslation();
  const [flipIndex, setFlipIndex] = useState(0);
  const [countries, setCountries] = useState([]);
  const [businessIsLoading, setBusinessIsLoading] = useState(false);
  const [states, setStates] = useState([]);

  const texts = [
    <>
      {t("BUSINESS.START_YOUR_JOURNEY_WITH_DUKANBUDDY")}
      <br />
      {t("BUSINESS.START_YOUR_JOURNEY_WITH_DUKANBUDDY2")}
    </>,
    <>
      {t("BUSINESS.EXPLORE_THE_POWER_OF_DUKANBUDDY")}
      <br />
      {t("BUSINESS.EXPLORE_THE_POWER_OF_DUKANBUDDY2")}
    </>,
  ];

  useEffect(() => {
    const countryList = Country.getAllCountries().map((country) => ({
      label: country.name,
      value: country.isoCode,
    }));
    const stateList = State.getStatesOfCountry("IN").map((state) => ({
      label: state.name,
      value: state.isoCode,
    }));

    setCountries(countryList);
    setStates(stateList);

    const interval = setInterval(() => {
      setFlipIndex((prevIndex) => (prevIndex + 1) % texts.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [texts.length]);

  const validationSchema = Yup.object().shape({
    businessName: Yup.string().required("Business Name is required"),
    phone: Yup.string()
      .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits")
      .required("Mobile Number is required"),
    businessCategory: Yup.string().required("Business Category is required"),
    businessOtherCategory: Yup.string().when('businessCategory', {
      is: 'other',
      then: schema => schema.required('Please specify your business category'),
      otherwise: schema => schema.notRequired(),
    }),
    pincode: Yup.string()
      .required("Pincode is required")
      .matches(/^\d{6}$/, "Pincode must be a 6-digit number"),
  });

  const initialValues = {
    businessName: "",
    phone: "",
    businessCategory: "no_select",
    businessOtherCategory: "",
    pincode: "",
    country: "IN",
    state: "BR",
    currency: "INR",
  };

  const handleSubmit = async (values) => {
    try {
      setBusinessIsLoading(true);
      await createBusiness(values);
    } catch (error) {
      console.error("Error creating business:", error);
    } finally {
      setBusinessIsLoading(false);
    }
  };

  const handleCountryChange = (setFieldValue, countryIsoCode) => {
    setFieldValue("country", countryIsoCode);
    const stateList = State.getStatesOfCountry(countryIsoCode || "IN").map(
      (state) => ({
        label: state.name,
        value: state.isoCode,
      })
    );
    setStates(stateList);
    setFieldValue("state", "");
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" }
    }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ rotateY: 0 }}
            animate={{ rotateY: flipIndex * 180 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 mb-4"
          >
            <FiBriefcase className="text-2xl text-white" />
          </motion.div>

          <motion.div
            key={flipIndex}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-2"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Setup Your Business
            </h2>
            <p className="text-gray-600 dark:text-gray-300 text-sm mt-2">
              {texts[flipIndex]}
            </p>
          </motion.div>

          <div className="flex justify-center space-x-1 mt-4">
            {texts.map((_, index) => (
              <div
                key={index}
                className={`w-1.5 h-1.5 rounded-full transition-all ${index === flipIndex
                  ? 'bg-blue-600 w-6'
                  : 'bg-gray-300 dark:bg-gray-600'
                  }`}
              />
            ))}
          </div>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            handleChange,
            setFieldValue,
            errors,
            handleBlur,
            touched,
          }) => (
            <Form>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerChildren}
                className="space-y-4"
              >
                {/* Business Name */}
                <motion.div variants={fadeInUp} className="space-y-1">
                  <label className={`text-sm font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-700"
                    }`}>
                    Business Name*
                  </label>
                  <div className="relative">
                    <input
                      type='text'
                      name='businessName'
                      placeholder='Enter your business name'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.businessName}
                      className={`w-full px-10 py-3 text-sm rounded-lg border outline-none transition-all ${theme === "dark"
                        ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30"
                        : "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20"
                        } ${errors.businessName && touched.businessName ? "border-red-500" : ""}`}
                      maxLength={128}
                    />
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <FiBriefcase className="text-gray-400 text-sm" />
                    </div>
                    {values.businessName && !errors.businessName && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                      >
                        <FiCheck className="text-green-500 text-xs" />
                      </motion.div>
                    )}
                  </div>
                  {errors.businessName && touched.businessName && (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-xs text-red-500"
                    >
                      {errors.businessName}
                    </motion.div>
                  )}
                </motion.div>

                {/* Mobile Number */}
                <motion.div variants={fadeInUp} className="space-y-1">
                  <label className={`text-sm font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-700"
                    }`}>
                    Mobile Number*
                  </label>
                  <div className="relative">
                    <input
                      type='tel'
                      name='phone'
                      placeholder='Enter your mobile number'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.phone}
                      maxLength={10}
                      className={`w-full px-10 py-3 text-sm rounded-lg border outline-none transition-all ${theme === "dark"
                        ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30"
                        : "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20"
                        } ${errors.phone && touched.phone ? "border-red-500" : ""}`}
                    />
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <FiPhone className="text-gray-400 text-sm" />
                    </div>
                    {values.phone && !errors.phone && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                      >
                        <FiCheck className="text-green-500 text-xs" />
                      </motion.div>
                    )}
                  </div>
                  {errors.phone && touched.phone && (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-xs text-red-500"
                    >
                      {errors.phone}
                    </motion.div>
                  )}
                </motion.div>

                {/* Business Category */}
                <motion.div variants={fadeInUp} className="space-y-1">
                  <label className={`text-sm font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-700"
                    }`}>
                    Business Category*
                  </label>
                  <div className="relative">
                    <select
                      name='businessCategory'
                      onChange={handleChange}
                      value={values.businessCategory}
                      className={`w-full px-10 py-3 text-sm rounded-lg border outline-none transition-all appearance-none cursor-pointer ${theme === "dark"
                        ? "bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30"
                        : "bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20"
                        } ${errors.businessCategory && touched.businessCategory ? "border-red-500" : ""}`}
                    >
                      <option value="no_select">Select a category</option>
                      {optionsForBusinessCategory.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <FiTrendingUp className="text-gray-400 text-sm" />
                    </div>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  {errors.businessCategory && touched.businessCategory && (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-xs text-red-500"
                    >
                      {errors.businessCategory}
                    </motion.div>
                  )}
                </motion.div>

                {/* Other Category Input (Conditional) */}
                {values.businessCategory === "Other" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    variants={fadeInUp}
                    className="space-y-1"
                  >
                    <label className={`text-sm font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-700"
                      }`}>
                      Specify Your Business Category*
                    </label>
                    <div className="relative">
                      <input
                        type='text'
                        name='businessOtherCategory'
                        placeholder='Enter your business category'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.businessOtherCategory}
                        className={`w-full px-10 py-3 text-sm rounded-lg border outline-none transition-all ${theme === "dark"
                          ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30"
                          : "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20"
                          } ${errors.businessOtherCategory ? "border-red-500" : ""}`}
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <FiTrendingUp className="text-gray-400 text-sm" />
                      </div>
                      {values.businessOtherCategory && !errors.businessOtherCategory && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        >
                          <FiCheck className="text-green-500 text-xs" />
                        </motion.div>
                      )}
                    </div>
                    {errors.businessOtherCategory && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-xs text-red-500"
                      >
                        {errors.businessOtherCategory}
                      </motion.div>
                    )}
                  </motion.div>
                )}

                {/* Country and State Row */}
                <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-3">
                  {/* Country */}
                  <div className="space-y-1">
                    <label className={`text-sm font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-700"
                      }`}>
                      Country
                    </label>
                    <div className="relative">
                      <select
                        name='country'
                        onChange={(e) => handleCountryChange(setFieldValue, e.target.value)}
                        value={values.country}
                        className={`w-full px-10 py-3 text-sm rounded-lg border outline-none transition-all appearance-none ${theme === "dark"
                          ? "bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30"
                          : "bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20"
                          }`}
                      >
                        {countries.map((country) => (
                          <option key={country.value} value={country.value}>
                            {country.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <FiGlobe className="text-gray-400 text-sm" />
                      </div>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* State */}
                  <div className="space-y-1">
                    <label className={`text-sm font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-700"
                      }`}>
                      State
                    </label>
                    <div className="relative">
                      <select
                        name='state'
                        onChange={(e) => setFieldValue("state", e.target.value)}
                        value={values.state}
                        className={`w-full px-10 py-3 text-sm rounded-lg border outline-none transition-all appearance-none cursor-pointer ${theme === "dark"
                          ? "bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30"
                          : "bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20"
                          }`}
                      >
                        {states.map((state) => (
                          <option key={state.value} value={state.value}>
                            {state.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <FiMapPin className="text-gray-400 text-sm" />
                      </div>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Pincode and Currency Row */}
                <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-3">
                  {/* Pincode */}
                  <div className="space-y-1">
                    <label className={`text-sm font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-700"
                      }`}>
                      Pincode*
                    </label>
                    <div className="relative">
                      <input
                        type='text'
                        name='pincode'
                        placeholder='Enter pincode'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.pincode}
                        onKeyPress={onlyNumber}
                        maxLength={6}
                        className={`w-full px-10 py-3 text-sm rounded-lg border outline-none transition-all ${theme === "dark"
                          ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30"
                          : "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20"
                          } ${errors.pincode && touched.pincode ? "border-red-500" : ""}`}
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <FiMapPin className="text-gray-400 text-sm" />
                      </div>
                      {values.pincode && !errors.pincode && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        >
                          <FiCheck className="text-green-500 text-xs" />
                        </motion.div>
                      )}
                    </div>
                    {errors.pincode && touched.pincode && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-xs text-red-500"
                      >
                        {errors.pincode}
                      </motion.div>
                    )}
                  </div>

                  {/* Currency */}
                  <div className="space-y-1">
                    <label className={`text-sm font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-700"
                      }`}>
                      Currency
                    </label>
                    <div className="relative">
                      <select
                        name='currency'
                        onChange={handleChange}
                        value={values.currency}
                        className={`w-full px-10 py-3 text-sm rounded-lg border outline-none transition-all appearance-none cursor-pointer ${theme === "dark"
                          ? "bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30"
                          : "bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20"
                          }`}
                      >
                        {CurrencySymbols.map((currency) => (
                          <option key={currency.value} value={currency.value}>
                            {currency.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <FiDollarSign className="text-gray-400 text-sm" />
                      </div>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </motion.div>


                {/* Submit Button */}
                <motion.button
                  variants={fadeInUp}
                  type='submit'
                  disabled={businessIsLoading}
                  whileHover={{ scale: businessIsLoading ? 1 : 1.02 }}
                  whileTap={{ scale: businessIsLoading ? 1 : 0.98 }}
                  className={`w-full py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${businessIsLoading
                    ? 'bg-blue-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl'
                    }`}
                >
                  {businessIsLoading ? (
                    <>
                      <BeatLoader color="#ffffff" size={8} />
                      <span>Setting up your business...</span>
                    </>
                  ) : (
                    <>
                      <span>Save & Continue</span>
                      <HiOutlineArrowRight className="text-base group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </motion.button>

                {/* Progress Indicator */}
                <motion.div
                  variants={fadeInUp}
                  className="pt-2 text-center"
                >
                  <div className="inline-flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span>Personal Info</span>
                    </div>
                    <FiChevronRight className="text-xs" />
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                      <span className="font-medium text-blue-600 dark:text-blue-400">Business Setup</span>
                    </div>
                    <FiChevronRight className="text-xs" />
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600"></div>
                      <span>Dashboard</span>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </Form>
          )}
        </Formik>
      </motion.div>
    </div>
  );
};

export default BusinessForm;